import React from 'react';

import {Task} from 'analyses/primers/PrimerWizardConstants';
import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface PrimerWizardSectionTask {
  task: Task;
  advanced: boolean;
}

interface PrimerWizardSectionRow {
  key: string;
  title: string;
  tasks: Array<PrimerWizardSectionTask>;
  component: JSX.Element;
}

export interface PrimerWizardSectionProps {
  title: string;
  rows: Array<PrimerWizardSectionRow>;
  open: boolean;
  task: string;
}

interface PrimerWizardSectionState {
  advanced: boolean;
  open: boolean;
}

export function isAdvancedSpecified(props: PrimerWizardSectionProps): boolean {
  return props.rows.some((row) => isRowAdvanced(row, props.task) && isRowSpecified(row));
}

export function isRowAdvanced(row: PrimerWizardSectionRow, task: string): boolean {
  for (const {task: rowTask, advanced} of Array.from(row.tasks)) {
    if (task === rowTask) {
      return advanced;
    }
  }
  return false;
}

export function isRowSpecified(row: PrimerWizardSectionRow): boolean {
  return row.component.props.inputs.some(
    (field: {value: string}) => field != null && field.value != null && field.value.length
  );
}

export function isSectionSpecified(props: PrimerWizardSectionProps): boolean {
  return props.rows.some((row) => isRowSpecified(row));
}

class PrimerWizardSection extends React.Component<PrimerWizardSectionProps, PrimerWizardSectionState> {
  constructor(props: PrimerWizardSectionProps) {
    super(props);
    this.state = {
      advanced: isAdvancedSpecified(props),
      open: props.open || isSectionSpecified(props),
    };
  }

  isRowApplicable(row: PrimerWizardSectionRow): boolean {
    return row.tasks.some(({task}) => task === this.props.task);
  }

  isSectionApplicable(): boolean {
    return this.props.rows.some((row) => this.isRowApplicable(row));
  }

  shouldShowRow(row: PrimerWizardSectionRow): boolean {
    return this.isRowApplicable(row) && (!isRowAdvanced(row, this.props.task) || this.state.advanced);
  }

  anyAdvanced(): boolean {
    return this.props.rows.some((row) => isRowAdvanced(row, this.props.task));
  }

  toggleOpen(): void {
    if (!this.state.open) {
      analytics.track('primer', 'expand_wizard_section', {
        product: ANALYTICS_MODULE.MOLBIO,
        section: this.props.title.toLowerCase().replace(/ /g, '_'),
      });
    }
    this.setState({open: !this.state.open});
  }

  toggleAdvanced(): void {
    this.setState({advanced: !this.state.advanced});
  }

  renderPanel(): JSX.Element | null {
    if (!this.state.open) {
      return null;
    }
    return (
      <div className="panel-collapse">
        <div className="panel-body">
          {(() => {
            const result = [];
            for (const row of Array.from(this.props.rows)) {
              if (this.shouldShowRow(row)) {
                result.push(
                  <FormGroup key={row.key}>
                    <Label md={2}>{row.title}</Label>
                    {row.component}
                  </FormGroup>
                );
              } else {
                result.push(undefined);
              }
            }
            return result;
          })()}
          {this.anyAdvanced() ? (
            <div className="u-spacing-top">
              <a className="pull-right" onClick={this.toggleAdvanced.bind(this)}>
                {this.state.advanced ? 'Hide advanced' : 'Show advanced'}
              </a>
            </div>
          ) : undefined}
        </div>
      </div>
    );
  }

  renderIcon(): JSX.Element {
    return (
      <BenchlingIcon
        icon={this.state.open ? 'minus-large' : 'plus-large'}
        className="u-small-spacing-right"
      />
    );
  }

  renderHeader(): JSX.Element {
    return (
      <div className="panel-heading u-pointer" onClick={this.toggleOpen.bind(this)}>
        <div className="panel-title">
          {this.renderIcon()}
          {this.props.title}
        </div>
      </div>
    );
  }

  render(): JSX.Element | null {
    if (!this.isSectionApplicable()) {
      return null;
    }
    return (
      <div className="panel panel-default">
        {this.renderHeader()}
        {this.renderPanel()}
      </div>
    );
  }
}

export default PrimerWizardSection;
